<template>
  <v-page page="bcmConstructFrameworks">
    <v-container fluid ma-5>
      <v-row no-gutters justify="center">
        <v-col cols="4" xs="12" sm="12" md="10" lg="6">
          <!-- BREADCRUMB -->
          <v-row dense>
            <v-col>
              <v-card flat>
                <v-breadcrumbs :items="breadcrumb">
                  <template #divider>
                    <v-icon>mdi-chevron-right</v-icon>
                  </template>
                </v-breadcrumbs>
                <v-card-text>
                  <disclaimer-panel>
                    <template v-slot:title>
                      This page is not fully featured yet, click to see more.
                    </template>
                    <template v-slot:content>
                      Not implemented yet (*):
                      <ul>
                        <li>Ability to create new construct frameworks</li>
                        <li>Construct framework profile pages</li>
                      </ul>
                    </template>
                  </disclaimer-panel>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- SEARCH BAR -->
          <v-row>
            <v-col>
              <v-card flat>
                <v-autocomplete
                  v-model="constructQuery"
                  :items="constructs"
                  label="Search for a construct..."
                  placeholder="Judging"
                  item-text="name"
                  item-value="name"
                  clearable
                  hide-details
                  outlined
                >
                  <template v-slot:item="item">
                    <v-list-item-content @click="$goConstructProfile(item.id)">
                      <v-list-item-title v-text="item.name"></v-list-item-title>
                      <v-list-item-subtitle
                        v-text="parseConstructType(item.construct_type_id)"
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-card>
            </v-col>
          </v-row>

          <!-- CONSTRUCT FRAMEWORKS -->
          <v-row>
            <v-col>
              <v-card>
                <v-data-table
                  :headers="constructTypeTableHeaders"
                  :items="constructTypes"
                  :search="constructTypeQuery"
                  dense
                  class="table-background"
                >
                  <template v-slot:top>
                    <v-toolbar flat color="#eee">
                      <v-text-field
                        v-model="constructTypeQuery"
                        append-icon="mdi-magnify"
                        label="Search for a construct framework..."
                        placeholder="Imbellus Problem Solving"
                        single-line
                        hide-details
                        clearable
                      ></v-text-field>
                      <v-spacer></v-spacer>
                      <v-btn class="primary">Create Construct Framework*</v-btn>
                    </v-toolbar>
                  </template>
                  <template v-slot:item="rowProps">
                    <tr
                      :active="rowProps.isSelected"
                      @click="selectConstructType(rowProps.item)"
                    >
                      <td>{{ rowProps.item.name }}</td>
                      <td>{{ formatDate(rowProps.item.created_at) }}</td>
                      <td>{{ formatDate(rowProps.item.updated_at) }}</td>
                      <td>
                        <v-icon
                          small
                          class="ml-3"
                        >
                          mdi-pencil
                        </v-icon>*
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>

          <!-- PRIMARY CONSTRUCTS -->
          <v-row>
            <v-col>
              <v-card flat v-if="isConstructTypeSelected" color="#eee">
                <v-card-title> Primary Constructs </v-card-title>
                <v-data-table
                  :headers="primaryConstructTableHeaders"
                  :items="primaryConstructs"
                  :search="primaryConstructQuery"
                  dense
                  class="table-background"
                >
                  <template v-slot:top>
                    <v-toolbar flat color="#eee">
                      <v-text-field
                        v-model="primaryConstructQuery"
                        append-icon="mdi-magnify"
                        label="Search for a primary construct..."
                        placeholder="Decision Making"
                        single-line
                        hide-details
                        clearable
                      ></v-text-field>
                      <v-spacer></v-spacer>
                      <construct-dialog
                        :constructs="constructs"
                        :constructTypeMap="getConstructTypeMap()"
                        @constructSaved="initialize"
                      ></construct-dialog>
                    </v-toolbar>
                  </template>
                  <template v-slot:item="rowProps">
                    <tr
                      :active="rowProps.item"
                      @click="selectPrimaryConstruct(rowProps.item)"
                    >
                      <td>{{ rowProps.item.name }}</td>
                      <td>{{ rowProps.item.definition }}</td>
                      <td>
                        <v-icon
                          small
                          class="ml-3"
                          @click="$goConstructProfile(rowProps.item.id)"
                        >
                          mdi-pencil
                        </v-icon>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>

          <!-- SECONDARY CONSTRUCTS -->
          <v-row>
            <v-col>
              <v-card flat v-if="isPrimaryConstructSelected" color="#eee">
                <v-card-title> Secondary Constructs </v-card-title>
                <v-data-table
                  :headers="secondaryConstructTableHeaders"
                  :items="secondaryConstructs"
                  :search="secondaryConstructQuery"
                  dense
                  class="table-background"
                >
                  <template v-slot:top>
                    <v-toolbar flat color="#eee">
                      <v-text-field
                        v-model="secondaryConstructQuery"
                        append-icon="mdi-magnify"
                        label="Search for a secondary construct..."
                        placeholder="Bias"
                        single-line
                        hide-details
                        clearable
                      ></v-text-field>
                      <v-spacer></v-spacer>
                      <construct-dialog
                        :constructs="constructs"
                        :constructTypeMap="getConstructTypeMap()"
                        @constructSaved="initialize"
                      ></construct-dialog>
                    </v-toolbar>
                  </template>
                  <template v-slot:item="rowProps">
                    <tr
                      :active="rowProps"
                      @click="selectSecondaryConstruct(rowProps.item)"
                    >
                      <td>{{ rowProps.item.name }}</td>
                      <td>
                        {{
                          parseParentConstructId(
                            rowProps.item.parent_construct_id
                          )
                        }}
                      </td>
                      <td>{{ rowProps.item.definition }}</td>
                      <td>
                        <v-icon
                          small
                          class="ml-3"
                          @click="$goConstructProfile(rowProps.item.id)"
                        >
                          mdi-pencil
                        </v-icon>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>

          <!-- TERTIARY CONSTRUCTS -->
          <v-row>
            <v-col>
              <v-card flat v-if="isSecondaryConstructSelected" color="#eee">
                <v-card-title> Tertiary Constructs </v-card-title>
                <v-data-table
                  :headers="tertiaryConstructTableHeaders"
                  :items="tertiaryConstructs"
                  :search="tertiaryConstructQuery"
                  dense
                  class="table-background"
                >
                  <template v-slot:top>
                    <v-toolbar flat color="#eee">
                      <v-text-field
                        v-model="tertiaryConstructQuery"
                        append-icon="mdi-magnify"
                        label="Search for a tertiary construct..."
                        placeholder="Recency"
                        single-line
                        hide-details
                        clearable
                      ></v-text-field>
                      <v-spacer></v-spacer>
                      <construct-dialog
                        :constructs="constructs"
                        :constructTypeMap="getConstructTypeMap()"
                        @constructSaved="initialize"
                      ></construct-dialog>
                    </v-toolbar>
                  </template>
                  <template #item="rowProps">
                    <tr :active="rowProps">
                      <td>{{ rowProps.item.name }}</td>
                      <td>
                        {{
                          parseParentConstructId(
                            rowProps.item.parent_construct_id
                          )
                        }}
                      </td>
                      <td>{{ rowProps.item.definition }}</td>
                      <td>
                        <v-icon
                          small
                          class="ml-3"
                          @click="$goConstructProfile(rowProps.item.id)"
                        >
                          mdi-pencil
                        </v-icon>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </v-page>
</template>

<script>
import VPage from '@/layouts/Default'
import ConstructDialog from '../components/constructs/dialog/CreateConstruct.vue'
import { Bus } from '@/bus'
import DisclaimerPanel from '../UI/DisclaimerPanel.vue'

export default {
  name: 'bcmConstructFrameworks',
  components: {
    VPage,
    ConstructDialog,
    DisclaimerPanel
  },
  data () {
    return {
      constructQuery: '',
      constructTypeQuery: '',
      primaryConstructQuery: '',
      secondaryConstructQuery: '',
      tertiaryConstructQuery: '',
      constructTypes: [],
      constructs: [],
      primaryConstructs: [],
      secondaryConstructs: [],
      tertiaryConstructs: [],
      selectedConstructType: {},
      selectedPrimaryConstruct: {},
      selectedSecondaryConstruct: {},
      isConstructTypeSelected: false,
      isPrimaryConstructSelected: false,
      isSecondaryConstructSelected: false,
      constructTypeTableHeaders: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Date Created', value: 'created_at', sortable: true },
        { text: 'Last Updated', value: 'updated_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      primaryConstructTableHeaders: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Definition', value: 'definition', sortable: true },
        { text: 'Actions', value: 'actions', sortable: true }
      ],
      secondaryConstructTableHeaders: [
        { text: 'Name', value: 'name', sortable: true },
        {
          text: 'Parent Construct',
          value: 'parent_construct_id',
          sortable: true
        },
        { text: 'Definition', value: 'definition', sortable: true },
        { text: 'Actions', value: 'actions', sortable: true }
      ],
      tertiaryConstructTableHeaders: [
        { text: 'Name', value: 'name', sortable: true },
        {
          text: 'Parent Construct',
          value: 'parent_construct_id',
          sortable: true
        },
        { text: 'Definition', value: 'definition', sortable: true },
        { text: 'Actions', value: 'actions', sortable: true }
      ],
      breadcrumb: [
        {
          text: 'Explore',
          disabled: false,
          exact: true,
          to: '/bcm'
        },
        {
          text: 'Construct Frameworks',
          disabled: false
        }
      ]
    }
  },
  async created () {
    await this.initialize()
  },
  methods: {
    async initialize () {
      await this.getConstructTypes()
      await this.getConstructs()
    },
    async getConstructTypes () {
      try {
        this.constructTypes = await this.$store.dispatch(
          'bcm/fetchConstructTypes',
          this.attribute ? { type: this.attribute, values: this.query } : null
        )
      } catch (err) {
        Bus.$emit('alert', {
          color: 'error',
          message:
            'Failed to retrieve Blueprint Content Manager Constructs Types'
        })
      }
    },
    async getConstructs () {
      try {
        this.constructs = await this.$store.dispatch(
          'bcm/fetchConstructs',
          this.attribute ? { type: this.attribute, values: this.query } : null
        )
      } catch (err) {
        Bus.$emit('alert', {
          color: 'error',
          message: 'Failed to retrieve Blueprint Content Manager Constructs'
        })
      }
    },
    selectConstructType (rowData) {
      this.selectedConstructType.id = rowData.id
      this.selectedConstructType.name = rowData.name
      this.selectedConstructType.createdAt = rowData.created_at
      this.selectedConstructType.updatedAt = rowData.updated_at
      this.isConstructTypeSelected = true
      this.isPrimaryConstructSelected = false
      this.isSecondaryConstructSelected = false
      this.primaryConstructs = this.constructs.filter(
        (construct) =>
          construct.parent_construct_id === null &&
          parseInt(construct.construct_type_id) ===
            parseInt(this.selectedConstructType.id)
      )
    },
    selectPrimaryConstruct (rowData) {
      this.selectedPrimaryConstruct.id = rowData.id
      this.selectedPrimaryConstruct.name = rowData.name
      this.selectedPrimaryConstruct.createdAt = rowData.created_at
      this.selectedPrimaryConstruct.updatedAt = rowData.updated_at
      this.isPrimaryConstructSelected = true
      this.isSecondaryConstructSelected = false
      this.secondaryConstructs = this.constructs.filter(
        (construct) =>
          parseInt(construct.parent_construct_id) ===
          parseInt(this.selectedPrimaryConstruct.id)
      )
    },
    selectSecondaryConstruct (rowData) {
      this.selectedSecondaryConstruct.id = rowData.id
      this.selectedSecondaryConstruct.name = rowData.name
      this.selectedSecondaryConstruct.createdAt = rowData.created_at
      this.selectedSecondaryConstruct.updatedAt = rowData.updated_at
      this.isSecondaryConstructSelected = true
      this.tertiaryConstructs = this.getSubConstructs(
        this.selectedSecondaryConstruct.id
      )
    },
    getSubConstructs (parentConstructId) {
      let rootSubConstructs = this.constructs.filter(
        (construct) =>
          parseInt(construct.parent_construct_id) ===
          parseInt(parentConstructId)
      )
      let subConstructs = rootSubConstructs
      let remainingConstructs = rootSubConstructs
      while (remainingConstructs.length > 0) {
        var newParentIds = remainingConstructs.map((construct) =>
          parseInt(construct.id)
        )
        remainingConstructs = this.constructs.filter((construct) =>
          newParentIds.includes(parseInt(construct.parent_construct_id))
        )
        if (remainingConstructs.length > 0) {
          subConstructs.push(...remainingConstructs)
        }
      }

      return subConstructs
    },
    parseParentConstructId (parentConstructId) {
      const matchedConstruct = this.constructs.find(
        (construct) => parseInt(construct.id) === parseInt(parentConstructId)
      )

      if (matchedConstruct) {
        return matchedConstruct.name
      } else {
        return 'None'
      }
    },
    getConstructTypeMap () {
      return [
        ...new Set(
          this.constructTypes.map((constructType) => ({
            name: constructType.name,
            construct_type_id: constructType.id
          }))
        )
      ]
    },
    parseConstructType (constructTypeId) {
      const matchedConstructType = this.constructTypes.find(
        (constructType) => constructType.id === parseInt(constructTypeId)
      )
      if (matchedConstructType) {
        return matchedConstructType.name
      }
    },
    formatDate (dateString) {
      var options = { year: 'numeric', month: 'long', day: 'numeric' }
      const parsedDate = new Date(dateString)
      return parsedDate.toLocaleString('en-US', options)
    }
  }
}
</script>

<style scoped>
.table-background {
  background-color: #eee;
}
</style>
